import type { ConfigAuditEntry } from '@servicetim/shared';
import {
  Badge,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
} from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import { useId, useMemo, useState } from 'react';
import { CopyButton } from '../common/copy-button';
import { formatDateTime, humanize } from '../common/format';
import { type DiffKind, diffJson, formatDiffValue, summarizeDiff } from './audit-diff';

const ACTION_VARIANT = { created: 'success', updated: 'info', deleted: 'destructive' } as const;

const KIND_CLASS: Record<DiffKind, string> = {
  added: 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-900 dark:text-emerald-300',
  removed: 'bg-red-50 dark:bg-red-500/15 text-red-900 dark:text-red-300',
  changed: 'bg-amber-50 dark:bg-amber-500/15 text-amber-900 dark:text-amber-300',
  unchanged: 'text-fg-muted',
};

const KIND_LABEL: Record<DiffKind, string> = {
  added: 'added',
  removed: 'removed',
  changed: 'changed',
  unchanged: 'unchanged',
};

export function AuditActionBadge({ action }: { action: ConfigAuditEntry['action'] }) {
  return <Badge variant={ACTION_VARIANT[action]}>{action}</Badge>;
}

/** Before/after of one audit entry as a leaf-level diff table, with the raw JSON underneath. */
export function AuditDiff({ entry }: { entry: ConfigAuditEntry }) {
  const id = useId();
  const [showUnchanged, setShowUnchanged] = useState(false);
  const entries = useMemo(() => diffJson(entry.before, entry.after), [entry.before, entry.after]);
  const summary = useMemo(() => summarizeDiff(entries), [entries]);
  const visible = showUnchanged ? entries : entries.filter((e) => e.kind !== 'unchanged');

  return (
    <div className="space-y-3" data-slot="audit-diff">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-emerald-700 dark:text-emerald-300">+{summary.added} added</span>
        <span className="text-red-700 dark:text-red-300">−{summary.removed} removed</span>
        <span className="text-amber-700 dark:text-amber-300">~{summary.changed} changed</span>
        <span className="text-fg-muted">{summary.unchanged} unchanged</span>
        <span className="ml-auto flex items-center gap-2">
          <Switch
            id={`${id}-unchanged`}
            checked={showUnchanged}
            onCheckedChange={setShowUnchanged}
          />
          <Label htmlFor={`${id}-unchanged`} className="font-normal">
            Show unchanged
          </Label>
        </span>
      </div>
      {visible.length === 0 ? (
        <p className="rounded-md bg-surface-sunken px-3 py-2 text-fg-muted text-sm">
          No differences between the snapshots.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <caption className="sr-only">Changes of this audit entry</caption>
            <thead className="bg-surface-sunken text-left text-fg-muted">
              <tr>
                <th scope="col" className="px-2 py-1.5 font-medium">
                  Field
                </th>
                <th scope="col" className="px-2 py-1.5 font-medium">
                  Before
                </th>
                <th scope="col" className="px-2 py-1.5 font-medium">
                  After
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map((row) => (
                <tr key={row.path} className={cn(KIND_CLASS[row.kind])} data-kind={row.kind}>
                  <th
                    scope="row"
                    className="whitespace-nowrap px-2 py-1.5 text-left font-mono font-normal"
                  >
                    {row.path}
                    <span className="sr-only"> ({KIND_LABEL[row.kind]})</span>
                  </th>
                  <td className="max-w-xs break-all px-2 py-1.5 font-mono">
                    {row.kind === 'added' ? '' : formatDiffValue(row.before)}
                  </td>
                  <td className="max-w-xs break-all px-2 py-1.5 font-mono">
                    {row.kind === 'removed' ? '' : formatDiffValue(row.after)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <details className="text-xs">
        <summary className="cursor-pointer text-fg-muted">Raw snapshots</summary>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <pre className="overflow-x-auto rounded-md bg-fg p-2 text-surface">
            {JSON.stringify(entry.before, null, 2) ?? 'null'}
          </pre>
          <pre className="overflow-x-auto rounded-md bg-fg p-2 text-surface">
            {JSON.stringify(entry.after, null, 2) ?? 'null'}
          </pre>
        </div>
      </details>
    </div>
  );
}

export function AuditDiffDrawer({
  entry,
  onClose,
}: {
  entry: ConfigAuditEntry | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={entry !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent className="overflow-y-auto sm:max-w-2xl">
        {entry ? (
          <>
            <SheetHeader>
              <SheetTitle className="flex flex-wrap items-center gap-2">
                <span className="capitalize">{humanize(entry.entityType)}</span>
                <AuditActionBadge action={entry.action} />
              </SheetTitle>
              <SheetDescription>
                {entry.actor ? entry.actor.displayName : 'System'} ·{' '}
                {formatDateTime(entry.occurredAt)}
              </SheetDescription>
            </SheetHeader>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-fg-muted">Entity id</dt>
              <dd className="flex items-center gap-2 font-mono text-xs">
                {entry.entityId}
                <CopyButton value={entry.entityId} size="sm" variant="ghost" label="Copy" />
              </dd>
              <dt className="text-fg-muted">Entry id</dt>
              <dd className="font-mono text-xs">{entry.id}</dd>
            </dl>
            <AuditDiff entry={entry} />
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
